import { useState, type ReactNode } from "react";
import { motion, AnimatePresence } from "motion/react";
import type { SleepSnapshot } from "@pfh/shared";
import { fmtMoney } from "@/lib/format";
import { cn } from "@/lib/utils";

/**
 * Compact "Asleep with these positions" badge for a leg whose worker
 * is currently asleep. Renders the snapshot summary inline; clicking
 * expands a popover with the full positions table the bot captured
 * just before closing Brave.
 *
 * The snapshot itself is captured by the worker's gracefulClose right
 * before context.close() runs (see worker.ts) and persisted on the
 * server (see services/bots/sleep-snapshots.ts). Without it, sleeping
 * a leg drops the live tick stream and the dashboard goes dark on
 * that leg until the operator wakes it again.
 *
 * Renders nothing when:
 *   - leg isn't asleep (`isAsleep` false), OR
 *   - no snapshot has been captured yet (e.g. bot was never asleep,
 *     or the snapshot capture timed out / threw)
 */
export function AsleepSnapshotBadge({
  isAsleep,
  snapshot,
  snapshotAt,
  className,
}: {
  isAsleep: boolean;
  snapshot: SleepSnapshot | null | undefined;
  snapshotAt: number | null | undefined;
  className?: string;
}): ReactNode {
  const [expanded, setExpanded] = useState(false);
  if (!isAsleep) return null;
  if (!snapshot || !snapshotAt) {
    return (
      <div
        className={cn(
          "rounded-md border border-border/50 bg-muted/30 px-2 py-1.5 text-[10px] text-muted-foreground",
          className,
        )}
      >
        Asleep — no pre-close snapshot recorded.
      </div>
    );
  }
  const ago = relativeTime(snapshotAt);
  const equityStr = snapshot.equity != null ? fmtMoney(snapshot.equity) : "—";
  const pnlColor =
    snapshot.openPnl >= 0 ? "text-emerald-500" : "text-destructive";
  return (
    <div
      className={cn(
        "rounded-md border border-border/50 bg-muted/30 p-2 text-[10px]",
        className,
      )}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setExpanded((v) => !v);
        }}
        className="flex w-full items-center gap-2 text-left"
      >
        <span className="font-medium uppercase tracking-wide text-muted-foreground">
          Asleep · {ago}
        </span>
        <span className="font-mono tabular-nums text-foreground">
          {snapshot.positionCount}p
        </span>
        <span className="font-mono tabular-nums text-foreground">
          {equityStr}
        </span>
        <span className={cn("ml-auto font-mono tabular-nums font-semibold", pnlColor)}>
          {snapshot.openPnl >= 0 ? "+" : ""}
          {fmtMoney(snapshot.openPnl)}
        </span>
      </button>
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="snapshot-detail"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="mt-2 space-y-1.5 border-t border-border/40 pt-2">
              <div className="grid grid-cols-3 gap-1.5 font-mono text-foreground/80">
                <Stat label="Balance" value={fmtNum(snapshot.balance)} />
                <Stat label="Equity" value={fmtNum(snapshot.equity)} />
                <Stat label="Margin Lvl" value={fmtNum(snapshot.marginLevel, "%")} />
                <Stat
                  label="Drawdown"
                  value={fmtNum(snapshot.drawdownUsed, "%")}
                />
                <Stat label="Daily DD" value={fmtNum(snapshot.dailyUsed, "%")} />
                <Stat
                  label="Closed P&L"
                  value={snapshot.closedPnl == null ? "—" : fmtMoney(snapshot.closedPnl)}
                />
              </div>
              {snapshot.openPositions.length > 0 ? (
                <div className="mt-1.5 overflow-x-auto">
                  <table className="w-full font-mono text-[9px]">
                    <thead className="text-muted-foreground/70">
                      <tr>
                        <th className="px-1 py-0.5 text-left">Symbol</th>
                        <th className="px-1 py-0.5 text-left">Side</th>
                        <th className="px-1 py-0.5 text-right">Lots</th>
                        <th className="px-1 py-0.5 text-right">Entry</th>
                        <th className="px-1 py-0.5 text-right">SL</th>
                        <th className="px-1 py-0.5 text-right">TP</th>
                        <th className="px-1 py-0.5 text-right">P&L</th>
                      </tr>
                    </thead>
                    <tbody>
                      {snapshot.openPositions.map((p) => (
                        <tr key={p.ticket} className="border-t border-border/30">
                          <td className="px-1 py-0.5 text-foreground">{p.symbol}</td>
                          <td
                            className={cn(
                              "px-1 py-0.5 uppercase",
                              p.side === "buy"
                                ? "text-emerald-500"
                                : "text-rose-500",
                            )}
                          >
                            {p.side}
                          </td>
                          <td className="px-1 py-0.5 text-right tabular-nums text-foreground">
                            {p.lots}
                          </td>
                          <td className="px-1 py-0.5 text-right tabular-nums text-foreground">
                            {p.entry}
                          </td>
                          <td className="px-1 py-0.5 text-right tabular-nums text-muted-foreground">
                            {p.stopLoss ?? "—"}
                          </td>
                          <td className="px-1 py-0.5 text-right tabular-nums text-muted-foreground">
                            {p.takeProfit ?? "—"}
                          </td>
                          <td
                            className={cn(
                              "px-1 py-0.5 text-right tabular-nums font-semibold",
                              p.openPnl >= 0
                                ? "text-emerald-500"
                                : "text-destructive",
                            )}
                          >
                            {p.openPnl >= 0 ? "+" : ""}
                            {fmtMoney(p.openPnl)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-muted-foreground">No open positions at sleep time.</p>
              )}
              <p className="font-mono text-muted-foreground/70">
                Captured {new Date(snapshot.capturedAt).toLocaleString()}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div className="flex items-center justify-between gap-1">
      <span className="text-muted-foreground/70">{label}</span>
      <span className="text-foreground">{value}</span>
    </div>
  );
}

function fmtNum(n: number | null | undefined, suffix = ""): string {
  if (n == null || !Number.isFinite(n)) return "—";
  return `${n.toFixed(2)}${suffix}`;
}

function relativeTime(ms: number): string {
  const diff = Date.now() - ms;
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}
